// src/pages/CoilDetail.js
// Phase-1 + Phase-2 Complete: Programs, Recovery, Dispatch, Finalization
// Phase-3A: OCR Upload integration for Recovery, Dispatch, Finalize
// Phase-3C: Export functionality for summaries and dispatch history

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Play, Pause, CheckCircle2, Circle, Scale, AlertTriangle,
  Layers, ChevronDown, ChevronUp, Calculator, Clock, Percent, Truck, Package,
  FileCheck, Lock
} from 'lucide-react';
import OCRUpload from '../components/OCRUpload';
import ExportMenu from '../components/ExportMenu';

// circular LED indicators
const ProgramStatusBadge = ({ status }) => {
  const config = {
    SETUP: { text: 'text-led-grey', bg: 'bg-led-grey' },
    RUNNING: { text: 'text-led-green', bg: 'bg-led-green' },
    PAUSED: { text: 'text-led-amber', bg: 'bg-led-amber' },
    COMPLETED: { text: 'text-led-green', bg: 'bg-led-green' },
  };
  const { text, bg } = config[status] || config.SETUP;
  return (
    <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold ${text}`}>
      <span className={`led-indicator ${bg}`} />
      {status}
    </div>
  );
};

const WeightStatusBadge = ({ status }) => {
  if (status === 'NET_CONFIRMED') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-green">
        <span className="led-indicator bg-led-green" />
        NET CONFIRMED
      </div>
    );
  }
  if (status === 'GROSS_ONLY') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-amber">
        <span className="led-indicator bg-led-amber" />
        GROSS ONLY
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-red">
      <span className="led-indicator bg-led-red" />
      WEIGHMENT PENDING
    </div>
  );
};

const RecoverySourceBadge = ({ source }) => {
  if (source === 'RECOVERY_LOG') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-green">
        <span className="led-indicator bg-led-green" />
        FROM LOG
      </div>
    );
  }
  if (source === 'EXPECTED_RECOVERY') {
    return (
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-amber">
        <span className="led-indicator bg-led-amber" />
        EXPECTED
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-grey">
      <span className="led-indicator bg-led-grey" />
      N/A
    </div>
  );
};

const ReconciliationStatusBadge = ({ status }) => {
  const config = {
    OPEN: { text: 'text-led-red', bg: 'bg-led-red', label: 'Open' },
    ESTIMATE_RUNNING: { text: 'text-led-amber', bg: 'bg-led-amber', label: 'Estimate Running' },
    READY_TO_CLOSE: { text: 'text-led-amber', bg: 'bg-led-amber', label: 'Ready to Close' },
    CLOSED: { text: 'text-led-green', bg: 'bg-led-green', label: 'Closed' },
  };
  const { text, bg, label } = config[status] || config.OPEN;
  return (
    <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold ${text}`}>
      <span className={`led-indicator ${bg}`} />
      {label}
    </div>
  );
};

// Phase-3D: Formatting helpers
const formatWeight = (val) => val != null ? Number(val).toFixed(3) : '-';
const formatRecovery = (val) => val != null ? Number(val).toFixed(2) : '-';
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const mins = String(d.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${mins}`;
};

const CoilDetail = () => {
  const { coilInId, coilId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  
  const [coilIn, setCoilIn] = useState(null);
  const [coilData, setCoilData] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [reconciliation, setReconciliation] = useState(null);
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Phase-3C: Export refs
  const summaryRef = useRef(null);
  const dispatchRef = useRef(null);
  const reconciliationRef = useRef(null);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [recoveryLogs, setRecoveryLogs] = useState({});
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(null);
  const [addingLog, setAddingLog] = useState(false);
  // Phase-3D: Extended recovery log with strokes, slit_width, photo
  const [newRecoveryLog, setNewRecoveryLog] = useState({ 
    recovery_percent: '', 
    strokes_per_min: '',
    slit_width_mm: '',
    photo_url: '',
    remarks: '' 
  });
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [addingDispatch, setAddingDispatch] = useState(false);
  const [newDispatch, setNewDispatch] = useState({ dispatch_date: new Date().toISOString().slice(0, 10), dispatched_weight_kg: '', remarks: '' });
  const [newProgram, setNewProgram] = useState({ circle_size_inch: '', expected_recovery_percent: '', job_rate_per_kg: '', remarks: '' });
  
  // Finalize dialog
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [finalizeData, setFinalizeData] = useState({
    final_circle_weight_kg: '',
    scrap_weight_kg: '',
    remarks: '',
  });
  const [settlements, setSettlements] = useState([]);
  const [showSettlementDialog, setShowSettlementDialog] = useState(false);
  const [recordingSettlement, setRecordingSettlement] = useState(false);
  const [settlementForm, setSettlementForm] = useState({
    settlement_type: 'CIRCLE_ADJUSTMENT',
    quantity_kg: '',
    rate_per_kg: '',
    reason: '',
    remarks: '',
  });
  
  // OCR states (Phase-3A)
  const [recoveryOcrData, setRecoveryOcrData] = useState({ rawText: '', fileUrl: '' });
  const [dispatchOcrData, setDispatchOcrData] = useState({ rawText: '', fileUrl: '' });
  const [finalizeOcrData, setFinalizeOcrData] = useState({ rawText: '', fileUrl: '' });
  
  // Phase-3D: Photo preview modal
  const [previewPhoto, setPreviewPhoto] = useState(null);
  
  const canManagePrograms = hasRole(['ADMIN', 'OWNER', 'MUNIM']);
  const canUpdateStatus = hasRole(['ADMIN', 'OWNER', 'MUNIM', 'OPERATOR']);
  const canAddRecoveryLogs = hasRole(['ADMIN', 'OWNER', 'MUNIM', 'OPERATOR']);
  const canDispatch = hasRole(['ADMIN', 'OWNER', 'MUNIM']);
  const canFinalize = hasRole(['ADMIN', 'OWNER', 'MUNIM']);

  // Check if coil is closed
  const isClosed = coilData?.lifecycle_state === 'CLOSED';

  const fetchReconciliation = useCallback(async () => {
    try {
      const res = await api.reconciliation.getSummary(coilInId, coilId);
      setReconciliation(res.data);
    } catch (error) {
      console.error('Failed to fetch reconciliation:', error);
    }
  }, [coilInId, coilId]);

  const fetchDispatches = useCallback(async () => {
    try {
      const res = await api.coilDispatch.list(coilInId, coilId);
      setDispatches(res.data || []);
    } catch (error) {
      console.error('Failed to fetch dispatches:', error);
    }
  }, [coilInId, coilId]);

  const fetchSettlements = useCallback(async () => {
    try {
      const res = await api.coilSettlements.list(coilInId, coilId);
      setSettlements(res.data || []);
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
      setSettlements([]);
    }
  }, [coilInId, coilId]);

  const fetchEstimate = useCallback(async () => {
    try {
      const res = await api.coilEstimates.get(coilInId, coilId);
      setEstimate(res.data);
    } catch (error) {
      console.error('Failed to fetch estimate:', error);
    }
  }, [coilInId, coilId]);

  const fetchRecoveryLogs = useCallback(async (programId) => {
    try {
      const res = await api.productionPrograms.getRecoveryLogs(programId);
      setRecoveryLogs(prev => ({ ...prev, [programId]: res.data || [] }));
    } catch (error) {
      console.error('Failed to fetch recovery logs:', error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const coilInRes = await api.coilIns.get(coilInId);
      setCoilIn(coilInRes.data);
      const coil = (coilInRes.data.coils || []).find(c => c.coil_id === coilId);
      setCoilData(coil);
      const programsRes = await api.productionPrograms.getByCoil(coilInId, coilId);
      setPrograms(programsRes.data || []);
      for (const prog of (programsRes.data || [])) {
        fetchRecoveryLogs(prog.id);
      }
      await Promise.all([fetchEstimate(), fetchReconciliation(), fetchDispatches(), fetchSettlements()]);
    } catch (error) {
      toast.error('Failed to fetch coil details');
    } finally {
      setLoading(false);
    }
  }, [coilInId, coilId, fetchEstimate, fetchReconciliation, fetchDispatches, fetchSettlements, fetchRecoveryLogs]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateProgram = async () => {
    if (!newProgram.circle_size_inch || !newProgram.job_rate_per_kg) { toast.error('Circle size and job rate are required'); return; }
    setCreating(true);
    try {
      await api.productionPrograms.create({
        coil_in_id: coilInId, coil_id: coilId,
        circle_size_inch: parseFloat(newProgram.circle_size_inch),
        expected_recovery_percent: newProgram.expected_recovery_percent ? parseFloat(newProgram.expected_recovery_percent) : null,
        job_rate_per_kg: parseFloat(newProgram.job_rate_per_kg),
        remarks: newProgram.remarks || null,
      });
      toast.success('Program created'); setShowCreateDialog(false);
      setNewProgram({ circle_size_inch: '', expected_recovery_percent: '', job_rate_per_kg: '', remarks: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create program'); }
    finally { setCreating(false); }
  };

  const handleStatusChange = async (programId, newStatus) => {
    try {
      await api.productionPrograms.update(programId, { status: newStatus });
      toast.success(`Program ${newStatus.toLowerCase()}`); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update status'); }
  };

  const handleAddRecoveryLog = async () => {
    if (!newRecoveryLog.recovery_percent) { toast.error('Recovery % required'); return; }
    const val = parseFloat(newRecoveryLog.recovery_percent);
    if (isNaN(val) || val <= 0 || val > 100) { toast.error('Recovery % must be 0-100'); return; }
    setAddingLog(true);
    try {
      const payload = { 
        recovery_percent: val, 
        remarks: newRecoveryLog.remarks || null,
        strokes_per_min: newRecoveryLog.strokes_per_min ? parseFloat(newRecoveryLog.strokes_per_min) : null,
        slit_width_mm: newRecoveryLog.slit_width_mm ? parseFloat(newRecoveryLog.slit_width_mm) : null,
        photo_url: newRecoveryLog.photo_url || recoveryOcrData.fileUrl || null,
      };
      await api.productionPrograms.addRecoveryLog(showRecoveryDialog, payload);
      
      if (recoveryOcrData.fileUrl && recoveryOcrData.rawText) {
        try {
          await api.ocr.feedback({
            context_type: 'RECOVERY_LOG',
            vendor_or_party_id: coilIn?.party_id || null,
            file_url: recoveryOcrData.fileUrl,
            raw_text: recoveryOcrData.rawText,
            extracted_fields: {},
            final_fields: {
              program_id: showRecoveryDialog,
              recovery_percent: val,
              strokes_per_min: payload.strokes_per_min,
              slit_width_mm: payload.slit_width_mm,
              remarks: newRecoveryLog.remarks,
            },
          });
        } catch (feedbackErr) {
          console.warn('OCR feedback submission failed (non-critical):', feedbackErr);
        }
      }
      
      toast.success('Recovery log added'); setShowRecoveryDialog(null);
      setNewRecoveryLog({ recovery_percent: '', strokes_per_min: '', slit_width_mm: '', photo_url: '', remarks: '' });
      setRecoveryOcrData({ rawText: '', fileUrl: '' });
      await fetchRecoveryLogs(showRecoveryDialog); await fetchEstimate(); await fetchReconciliation();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setAddingLog(false); }
  };

  const handleAddDispatch = async () => {
    if (!newDispatch.dispatched_weight_kg) { toast.error('Weight required'); return; }
    const val = parseFloat(newDispatch.dispatched_weight_kg);
    if (isNaN(val) || val <= 0) { toast.error('Weight must be > 0'); return; }
    setAddingDispatch(true);
    try {
      const dispatchDate = newDispatch.dispatch_date ? new Date(newDispatch.dispatch_date).toISOString() : new Date().toISOString();
      await api.coilDispatch.add(coilInId, coilId, { dispatch_date: dispatchDate, dispatched_weight_kg: val, remarks: newDispatch.remarks || null });
      
      if (dispatchOcrData.fileUrl && dispatchOcrData.rawText) {
        try {
          await api.ocr.feedback({
            context_type: 'DISPATCH',
            vendor_or_party_id: coilIn?.party_id || null,
            file_url: dispatchOcrData.fileUrl,
            raw_text: dispatchOcrData.rawText,
            extracted_fields: {},
            final_fields: {
              dispatch_date: dispatchDate,
              dispatched_weight_kg: val,
              remarks: newDispatch.remarks,
            },
          });
        } catch (feedbackErr) {
          console.warn('OCR feedback submission failed (non-critical):', feedbackErr);
        }
      }
      
      toast.success('Dispatch added'); setShowDispatchDialog(false);
      setNewDispatch({ dispatch_date: new Date().toISOString().slice(0, 10), dispatched_weight_kg: '', remarks: '' });
      setDispatchOcrData({ rawText: '', fileUrl: '' });
      await fetchDispatches(); await fetchReconciliation();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setAddingDispatch(false); }
  };

  const handleFinalize = async () => {
    if (!finalizeData.final_circle_weight_kg) {
      toast.error('Enter final circle weight'); return;
    }
    if (!finalizeData.scrap_weight_kg) {
      toast.error('Enter final scrap weight'); return;
    }
    setFinalizing(true);
    try {
      const payload = {
        final_circle_weight_kg: parseFloat(finalizeData.final_circle_weight_kg),
        scrap_weight_kg: parseFloat(finalizeData.scrap_weight_kg),
        remarks: finalizeData.remarks || null,
      };
      await api.coilFinalize.finalize(coilInId, coilId, payload);
      
      if (finalizeOcrData.fileUrl && finalizeOcrData.rawText) {
        try {
          await api.ocr.feedback({
            context_type: 'FINALIZE',
            vendor_or_party_id: coilIn?.party_id || null,
            file_url: finalizeOcrData.fileUrl,
            raw_text: finalizeOcrData.rawText,
            extracted_fields: {},
            final_fields: {
              final_circle_weight_kg: payload.final_circle_weight_kg,
              scrap_weight_kg: payload.scrap_weight_kg,
              remarks: payload.remarks,
            },
          });
        } catch (feedbackErr) {
          console.warn('OCR feedback submission failed (non-critical):', feedbackErr);
        }
      }
      
      toast.success('Coil finalization saved'); setShowFinalizeDialog(false);
      setFinalizeData({ final_circle_weight_kg: '', scrap_weight_kg: '', remarks: '' });
      setFinalizeOcrData({ rawText: '', fileUrl: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to finalize'); }
    finally { setFinalizing(false); }
  };

  const handleRecordSettlement = async () => {
    if (!settlementForm.quantity_kg) { toast.error('Enter settlement quantity'); return; }
    const qty = parseFloat(settlementForm.quantity_kg);
    if (isNaN(qty) || qty <= 0) { toast.error('Settlement quantity must be > 0'); return; }
    const rate = settlementForm.rate_per_kg !== '' ? parseFloat(settlementForm.rate_per_kg) : null;
    if (rate != null && (isNaN(rate) || rate < 0)) { toast.error('Rate must be >= 0'); return; }
    setRecordingSettlement(true);
    try {
      await api.coilSettlements.create(coilInId, coilId, {
        settlement_type: settlementForm.settlement_type,
        quantity_kg: qty,
        rate_per_kg: rate,
        reason: settlementForm.reason || null,
        remarks: settlementForm.remarks || null,
      });
      toast.success('Settlement recorded');
      setShowSettlementDialog(false);
      setSettlementForm({
        settlement_type: 'CIRCLE_ADJUSTMENT',
        quantity_kg: '',
        rate_per_kg: '',
        reason: '',
        remarks: '',
      });
      await Promise.all([fetchReconciliation(), fetchSettlements(), fetchData()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record settlement');
    } finally {
      setRecordingSettlement(false);
    }
  };

  const toggleProgramExpand = (programId) => { setExpandedPrograms(prev => ({ ...prev, [programId]: !prev[programId] })); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-industrial-bg text-white">
        <div className="animate-spin rounded-none h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!coilData) {
    return (
      <div className="space-y-4 p-6 min-h-screen bg-industrial-bg text-white">
        <button className="text-slate-400 hover:text-white font-mono text-xs uppercase flex items-center gap-1.5" onClick={() => navigate('/coil-in')}>
          <ArrowLeft className="h-4 w-4" /> BACK
        </button>
        <div className="bg-industrial-s1 border border-industrial-border p-6 text-center text-slate-400 font-mono">
          COIL NOT FOUND
        </div>
      </div>
    );
  }

  const runningProgram = programs.find(p => p.status === 'RUNNING');
  const totalDispatched = reconciliation?.total_dispatched_kg ?? dispatches.reduce((sum, d) => sum + (d.dispatched_weight_kg || 0), 0);
  const isOverDispatched = reconciliation?.over_dispatched || false;

  return (
    <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6" data-testid="coil-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-industrial-border pb-4">
        <div className="flex items-center gap-4">
          <button className="text-slate-400 hover:text-white font-mono text-xs uppercase flex items-center gap-1.5 border border-industrial-border px-3 py-1.5 bg-industrial-s1 hover:bg-neutral-800" onClick={() => navigate('/coil-in')}>
            <ArrowLeft className="h-4 w-4" /> BACK
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold tracking-tight uppercase">Coil Detail</h1>
              {isClosed && (
                <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-led-green bg-industrial-s1 border border-industrial-border px-2 py-0.5">
                  <span className="led-indicator bg-led-green" /> CLOSED
                </div>
              )}
            </div>
            <p className="text-sm font-mono text-slate-400 mt-1">{coilIn?.party_name} • SLIP: {coilIn?.slip_no || 'No Slip'}</p>
          </div>
        </div>
        
        {/* Finalize Button */}
        {canFinalize && !isClosed && reconciliation?.reconciliation_status !== 'OPEN' && (
          <button 
            onClick={() => setShowFinalizeDialog(true)} 
            className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-4 flex items-center justify-center gap-1.5"
            data-testid="finalize-btn"
          >
            <Lock className="h-4 w-4" /> FINALIZE & CLOSE COIL
          </button>
        )}
      </div>

      {/* CLOSED Banner */}
      {isClosed && (
        <div className="border-l-4 border-led-green bg-industrial-s1 p-4 font-mono text-sm text-slate-300">
          <div className="flex items-center gap-2 font-bold text-white uppercase text-xs mb-1">
            <Lock className="h-4 w-4 text-led-green" /> COIL CLOSED
          </div>
          This coil is finalized. All data is now read-only. 
          {coilData.finalized_at && <span className="text-slate-400 block md:inline md:ml-2">FINALIZED ON: {new Date(coilData.finalized_at).toLocaleString()}</span>}
        </div>
      )}

      {/* Top Row: Coil Info + Reconciliation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Coil Information */}
        <div className="bg-industrial-s1 border border-industrial-border p-6" ref={summaryRef}>
          <div className="flex items-center justify-between border-b border-industrial-border pb-3 mb-4">
            <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 flex items-center gap-2">
              <Scale className="h-4 w-4" /> Coil Information
            </h2>
            {isClosed && (
              <ExportMenu
                elementRef={summaryRef}
                coilNo={coilId}
                title="Coil Final Summary"
                prefix="CoilSummary"
                showCSV={true}
                csvData={[{
                  coil_id: coilId,
                  thickness_mm: coilData?.thickness_mm,
                  coil_width_mm: coilData?.coil_width_mm,
                  charge_weight_kg: coilData?.charge_weight_kg || coilData?.party_declared_weight_kg,
                  final_circle_weight_kg: coilData?.final_circle_weight_kg,
                  scrap_weight_kg: reconciliation?.scrap_weight_kg,
                  total_dispatched_kg: reconciliation?.total_dispatched_kg,
                  lifecycle_state: coilData?.lifecycle_state,
                  finalized_at: coilData?.finalized_at,
                }]}
                csvColumns={[
                  { key: 'coil_id', label: 'Coil ID' },
                  { key: 'thickness_mm', label: 'Thickness (mm)' },
                  { key: 'coil_width_mm', label: 'Width (mm)' },
                  { key: 'charge_weight_kg', label: 'Charge Weight (kg)' },
                  { key: 'final_circle_weight_kg', label: 'Final Circle (kg)' },
                  { key: 'scrap_weight_kg', label: 'Scrap (kg)' },
                  { key: 'total_dispatched_kg', label: 'Total Dispatched (kg)' },
                  { key: 'lifecycle_state', label: 'Status' },
                  { key: 'finalized_at', label: 'Finalized At' },
                ]}
              />
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-y-4 gap-x-6">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Thickness</p>
              <p className="font-mono text-white text-base font-bold">{coilData.thickness_mm} mm</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Width</p>
              <p className="font-mono text-white text-base font-bold">{coilData.coil_width_mm} mm</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Charge Weight</p>
              <p className="font-mono text-white text-base font-bold">{formatWeight(coilData.charge_weight_kg || coilData.party_declared_weight_kg)} kg</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Weight Status</p>
              <div className="mt-1">
                <WeightStatusBadge status={coilData.weight_status} />
              </div>
            </div>
          </div>
          
          {isClosed && coilData.final_circle_weight_kg && (
            <div className="mt-6 pt-4 border-t border-industrial-border">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Final Circle Weight</p>
              <p className="font-mono text-2xl font-bold text-led-green">{formatWeight(coilData.final_circle_weight_kg)} kg</p>
            </div>
          )}
          
          <div className="mt-6 flex gap-2 flex-wrap">
            <span className={`px-2 py-0.5 font-mono text-[10px] uppercase border ${isClosed ? 'border-led-green/40 text-led-green bg-led-green/5' : 'border-industrial-border text-slate-400 bg-industrial-s2'}`}>
              LIFECYCLE: {coilData.lifecycle_state || 'OPEN'}
            </span>
            {coilData.charge_weight_source === 'PARTY_DECLARED' && (
              <span className="px-2 py-0.5 font-mono text-[10px] uppercase border border-led-red/40 text-led-red bg-led-red/5">
                RISK: PARTY WEIGHT USED
              </span>
            )}
          </div>
        </div>

        {/* Reconciliation Summary */}
        <div className="bg-industrial-s1 border border-industrial-border p-6" ref={reconciliationRef}>
          <div className="flex items-center justify-between border-b border-industrial-border pb-3 mb-4">
            <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 flex items-center gap-2">
              <FileCheck className="h-4 w-4" /> Reconciliation Summary
            </h2>
            <div className="flex items-center gap-3">
              {reconciliation && <ReconciliationStatusBadge status={reconciliation.reconciliation_status} />}
              <ExportMenu
                elementRef={reconciliationRef}
                coilNo={coilId}
                title="Reconciliation Summary"
                prefix="Recon"
                disabled={!reconciliation}
              />
            </div>
          </div>

          {reconciliation ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-xs font-mono">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Charge Weight</p>
                  <p className="text-white font-bold">{formatWeight(reconciliation.charge_weight_kg)} kg</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">{reconciliation.final_circle_weight_kg ? 'Final Circle' : 'Estimated Circle'}</p>
                  <p className="text-white font-bold">{formatWeight(reconciliation.final_circle_weight_kg || reconciliation.estimated_circle_kg)} kg</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Total Dispatched</p>
                  <p className="text-white font-bold">{formatWeight(reconciliation.total_dispatched_kg || 0)} kg</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Balance Circle</p>
                  <p className={`font-bold ${reconciliation.over_dispatched ? 'text-led-red' : 'text-led-green'}`}>{formatWeight(reconciliation.balance_circle_kg)} kg</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-y-3 gap-x-4 border-t border-industrial-border/60 pt-3 text-xs font-mono">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Actual Recovery %</p>
                  <p className="text-white font-bold">{formatRecovery(reconciliation.actual_recovery_percent)}%</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Material Balance Pending</p>
                  <p className="text-white font-bold">{formatWeight(reconciliation.material_balance_kg)} kg</p>
                </div>
              </div>

              <div className="pt-3 border-t border-industrial-border/60 grid grid-cols-3 gap-2 text-xs font-mono">
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Final Scrap</p>
                  <p className="text-white font-bold">{formatWeight(reconciliation.final_scrap_weight_kg ?? reconciliation.scrap_weight_kg)} kg</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Scrap Disp</p>
                  <p className="text-white font-bold">{formatWeight(reconciliation.scrap_dispatched_kg)} kg</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500 font-sans mb-0.5">Scrap Pending</p>
                  <p className="text-white font-bold">{formatWeight(reconciliation.scrap_pending_kg)} kg</p>
                </div>
              </div>

              <div className="pt-3 border-t border-industrial-border/60">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Closure Blockers</p>
                  {canFinalize && !isClosed && (
                    <button 
                      onClick={() => setShowSettlementDialog(true)}
                      className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-[10px] uppercase tracking-wider h-7 px-3 flex items-center justify-center transition-colors"
                    >
                      RECORD SETTLEMENT
                    </button>
                  )}
                </div>
                
                {(reconciliation.closure_blockers || []).length === 0 ? (
                  <p className="text-xs text-led-green font-mono">No blockers. Ready for finalization.</p>
                ) : (
                  <ul className="text-xs text-led-red font-mono space-y-1 list-none">
                    {(reconciliation.closure_blockers || []).map((b, idx) => (
                      <li key={`${b}-${idx}`} className="flex items-start gap-1">
                        <span className="text-led-red">■</span> {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {settlements.length > 0 && (
                <div className="pt-3 border-t border-industrial-border/60">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Recent Settlements</p>
                  <div className="space-y-1 font-mono text-[11px]">
                    {settlements.slice(0, 3).map((s) => (
                      <div key={s.id} className="text-slate-400 flex justify-between bg-industrial-s2 px-2 py-1 border border-industrial-border/30">
                        <span>{s.settlement_type} ({formatWeight(s.quantity_kg)} kg)</span>
                        <span className="text-white font-bold">
                          {s.amount != null ? `₹${Number(s.amount).toFixed(2)}` : (s.rate_per_kg != null ? `₹${Number(s.rate_per_kg).toFixed(2)}/kg` : 'Recorded')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-[10px] text-slate-500 italic mt-1 font-mono">{reconciliation.status_reason}</p>
            </div>
          ) : <p className="text-slate-400 font-mono text-xs">Loading data...</p>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="programs" className="w-full">
        <TabsList className="flex border-b border-industrial-border bg-transparent p-0 gap-2 mb-4">
          <TabsTrigger 
            value="programs" 
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-none border-b-2 border-transparent text-slate-400 data-[state=active]:border-accent data-[state=active]:text-white data-[state=active]:bg-industrial-s1"
          >
            <span className="flex items-center gap-2"><Layers className="h-3.5 w-3.5" /> Programs ({programs.length}/3)</span>
          </TabsTrigger>
          <TabsTrigger 
            value="dispatch" 
            className="px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-none border-b-2 border-transparent text-slate-400 data-[state=active]:border-accent data-[state=active]:text-white data-[state=active]:bg-industrial-s1"
          >
            <span className="flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> Dispatch ({dispatches.length})</span>
          </TabsTrigger>
        </TabsList>

        {/* Programs Tab */}
        <TabsContent value="programs" className="mt-0 space-y-4">
          {estimate && !isClosed && (
            <div className="border border-industrial-border bg-industrial-s1 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 font-mono text-sm">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-led-amber" />
                  <span className="text-slate-400">ESTIMATED CIRCLE:</span>
                  <span className="font-bold text-base text-led-green">{formatWeight(estimate.estimated_circle_kg)} kg</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">RECOVERY: {formatRecovery(estimate.recovery_percent_used)}%</span>
                  <RecoverySourceBadge source={estimate.recovery_source} />
                </div>
              </div>
              {estimate.latest_log_timestamp && (
                <p className="text-[10px] text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> LAST UPDATED: {formatDateTime(estimate.latest_log_timestamp)}
                </p>
              )}
            </div>
          )}

          {runningProgram && !isClosed && (
            <div className="border-l-4 border-led-green bg-industrial-s1 p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <span className="led-indicator bg-led-green animate-pulse" />
                <div>
                  <p className="font-mono font-bold text-white">PROGRAM #{runningProgram.program_no} ACTIVE</p>
                  <p className="text-xs font-mono text-slate-400 mt-0.5">{runningProgram.circle_size_inch}" circle size @ ₹{runningProgram.job_rate_per_kg}/kg</p>
                </div>
              </div>
              {canUpdateStatus && (
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleStatusChange(runningProgram.id, 'PAUSED')}
                    className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-9 px-3 flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <Pause className="h-3.5 w-3.5" /> PAUSE
                  </button>
                  <button 
                    onClick={() => handleStatusChange(runningProgram.id, 'COMPLETED')}
                    className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-9 px-3 flex items-center justify-center gap-1.5 transition-colors"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> COMPLETE
                  </button>
                </div>
              )}
            </div>
          )}

          {canManagePrograms && programs.length < 3 && !isClosed && (
            <div>
              <button 
                onClick={() => setShowCreateDialog(true)} 
                className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-4 flex items-center justify-center gap-1.5"
                data-testid="add-program-btn"
              >
                <Plus className="h-4 w-4" /> CREATE / SCHEDULE PROGRAM
              </button>
            </div>
          )}

          {programs.length === 0 ? (
            <div className="bg-industrial-s1 border border-industrial-border p-8 text-center space-y-4">
              <p className="text-slate-400 font-mono text-sm">NO SCHEDULING PROGRAMS DEFINED YET.</p>
              {canManagePrograms && !isClosed && (
                <button 
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6 mx-auto flex items-center justify-center gap-1.5"
                >
                  <Plus className="h-4 w-4" /> CREATE FIRST SCHEDULED PROGRAM
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {programs.map((program) => {
                const logs = recoveryLogs[program.id] || [];
                const isExpanded = expandedPrograms[program.id];
                return (
                  <div key={program.id} className={`bg-industrial-s1 border ${program.status === 'RUNNING' ? 'border-led-green' : 'border-industrial-border'} p-4`}>
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 pb-4">
                      <div className="space-y-3 flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-mono font-bold text-white text-base">Program #{program.program_no}</span>
                          <ProgramStatusBadge status={program.status} />
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-mono">
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-sans">Circle Size</p>
                            <p className="text-white font-bold text-sm mt-0.5">{program.circle_size_inch}"</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-sans">Job Rate</p>
                            <p className="text-white font-bold text-sm mt-0.5">₹{program.job_rate_per_kg}/kg</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-sans">Expected Recovery</p>
                            <p className="text-white font-bold text-sm mt-0.5">{program.expected_recovery_percent ? `${formatRecovery(program.expected_recovery_percent)}%` : '-'}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-500 uppercase tracking-wider font-sans">Recovery Logs</p>
                            <p className="text-white font-bold text-sm mt-0.5">{logs.length}</p>
                          </div>
                        </div>
                      </div>

                      {canUpdateStatus && program.status !== 'COMPLETED' && !isClosed && (
                        <div className="flex gap-2 self-start md:self-auto">
                          {(program.status === 'SETUP' || program.status === 'PAUSED') && !runningProgram && (
                            <button 
                              onClick={() => handleStatusChange(program.id, 'RUNNING')}
                              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-9 px-3 flex items-center justify-center gap-1.5 transition-colors"
                            >
                              <Play className="h-3.5 w-3.5" /> {program.status === 'PAUSED' ? 'RESUME' : 'START'}
                            </button>
                          )}
                          {program.status === 'RUNNING' && (
                            <>
                              <button 
                                onClick={() => handleStatusChange(program.id, 'PAUSED')}
                                className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-9 px-3 flex items-center justify-center gap-1.5 transition-colors"
                              >
                                <Pause className="h-3.5 w-3.5" /> PAUSE
                              </button>
                              <button 
                                onClick={() => handleStatusChange(program.id, 'COMPLETED')}
                                className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-9 px-3 flex items-center justify-center gap-1.5 transition-colors"
                              >
                                <CheckCircle2 className="h-3.5 w-3.5" /> COMPLETE
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <Collapsible open={isExpanded} onOpenChange={() => toggleProgramExpand(program.id)}>
                      <div className="pt-3 border-t border-industrial-border/50">
                        <div className="flex items-center justify-between">
                          <CollapsibleTrigger asChild>
                            <button className="text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-white flex items-center gap-1 border border-industrial-border/60 bg-industrial-s2 px-3 py-1.5">
                              <Percent className="h-3.5 w-3.5 text-slate-500" />
                              RECOVERY LOGS ({logs.length})
                              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                            </button>
                          </CollapsibleTrigger>
                          
                          {canAddRecoveryLogs && (program.status === 'RUNNING' || program.status === 'PAUSED') && !isClosed && (
                            <button 
                              onClick={() => setShowRecoveryDialog(program.id)}
                              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-[10px] uppercase tracking-wider h-7 px-3 flex items-center justify-center gap-1 transition-colors"
                            >
                              <Plus className="h-3 w-3" /> ADD LOG
                            </button>
                          )}
                        </div>
                        
                        <CollapsibleContent className="mt-3">
                          {logs.length === 0 ? (
                            <div className="text-center py-6 border border-dashed border-industrial-border bg-industrial-s2/30">
                              <p className="text-xs text-slate-400 font-mono mb-3">NO RECOVERY LOGS RECORDED.</p>
                              {canAddRecoveryLogs && (program.status === 'RUNNING' || program.status === 'PAUSED') && !isClosed && (
                                <button 
                                  onClick={() => setShowRecoveryDialog(program.id)}
                                  className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-[10px] uppercase tracking-wider h-8 px-4 inline-flex items-center justify-center gap-1.5"
                                >
                                  <Plus className="h-3.5 w-3.5" /> ADD FIRST LOG
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="overflow-x-auto border border-industrial-border bg-industrial-s2/20">
                              <table className="w-full text-xs font-mono text-left">
                                <thead>
                                  <tr className="bg-industrial-s2/50 border-b border-industrial-border text-slate-400 text-[9px] uppercase tracking-wider">
                                    <th className="px-3 py-2">Date/Time</th>
                                    <th className="px-3 py-2">Recovery %</th>
                                    <th className="px-3 py-2 text-right">Strokes/min</th>
                                    <th className="px-3 py-2 text-right">Slit Width (mm)</th>
                                    <th className="px-3 py-2 text-center">Photo</th>
                                    <th className="px-3 py-2">Remarks</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {logs.map((log, idx) => (
                                    <tr key={log.id} className={`border-b border-industrial-border/30 hover:bg-industrial-s2/40 ${idx % 2 === 0 ? 'bg-transparent' : 'bg-industrial-s2/10'}`}>
                                      <td className="px-3 py-2 text-slate-300">{formatDateTime(log.timestamp)}</td>
                                      <td className="px-3 py-2 text-white font-bold">{formatRecovery(log.recovery_percent)}%</td>
                                      <td className="px-3 py-2 text-right text-slate-300">{log.strokes_per_min || '-'}</td>
                                      <td className="px-3 py-2 text-right text-slate-300">{log.slit_width_mm || '-'}</td>
                                      <td className="px-3 py-2 text-center">
                                        {log.photo_url ? (
                                          <button 
                                            type="button"
                                            onClick={() => setPreviewPhoto(log.photo_url)}
                                            className="text-accent hover:underline text-[10px] uppercase font-bold"
                                          >
                                            VIEW
                                          </button>
                                        ) : '-'}
                                      </td>
                                      <td className="px-3 py-2 text-slate-400 max-w-[150px] truncate">{log.remarks || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Dispatch Tab */}
        <TabsContent value="dispatch" className="mt-0 space-y-4">
          {isOverDispatched && (
            <div className="border-l-4 border-led-red bg-industrial-s1 p-4 font-mono text-sm text-slate-300">
              <div className="flex items-center gap-2 font-bold text-white uppercase text-xs mb-1">
                <AlertTriangle className="h-4 w-4 text-led-red animate-pulse" /> RISK: OVER-DISPATCH
              </div>
              Total dispatched weight exceeds available circle weight. Review allocations.
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-industrial-s1 border border-industrial-border p-4 flex items-center gap-3">
              <Truck className="h-8 w-8 text-slate-400" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans">Total Dispatched</p>
                <p className="font-mono font-bold text-xl text-white">{formatWeight(totalDispatched)} kg</p>
              </div>
            </div>
            <div className={`bg-industrial-s1 border p-4 flex items-center gap-3 ${isOverDispatched ? 'border-led-red' : 'border-industrial-border'}`}>
              <Package className="h-8 w-8 text-slate-400" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans">Balance Circle</p>
                <p className={`font-mono font-bold text-xl ${isOverDispatched ? 'text-led-red' : 'text-led-green'}`}>{formatWeight(reconciliation?.balance_circle_kg)} kg</p>
              </div>
            </div>
            <div className="bg-industrial-s1 border border-industrial-border p-4 flex items-center gap-3">
              <FileCheck className="h-8 w-8 text-slate-400" />
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-sans">Scrap Pending</p>
                <p className="font-mono font-bold text-xl text-white">{formatWeight(reconciliation?.scrap_pending_kg)} kg</p>
              </div>
            </div>
          </div>
          
          {canDispatch && !isClosed && (
            <div>
              <button 
                onClick={() => setShowDispatchDialog(true)} 
                className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-4 flex items-center justify-center gap-1.5"
                data-testid="add-dispatch-btn"
              >
                <Plus className="h-4 w-4" /> ADD DISPATCH
              </button>
            </div>
          )}

          <div className="bg-industrial-s1 border border-industrial-border p-6" ref={dispatchRef}>
            <div className="flex items-center justify-between border-b border-industrial-border pb-3 mb-4">
              <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400">Dispatch History</h2>
              <ExportMenu
                elementRef={dispatchRef}
                coilNo={coilId}
                title="Dispatch History"
                prefix="Dispatch"
                disabled={dispatches.length === 0}
                showCSV={true}
                csvData={dispatches.map(d => ({
                  dispatch_date: formatDate(d.dispatch_date),
                  dispatched_weight_kg: formatWeight(d.dispatched_weight_kg),
                  remarks: d.remarks || '',
                  created_at: formatDateTime(d.created_at),
                }))}
                csvColumns={[
                  { key: 'dispatch_date', label: 'Dispatch Date' },
                  { key: 'dispatched_weight_kg', label: 'Weight (kg)' },
                  { key: 'remarks', label: 'Remarks' },
                  { key: 'created_at', label: 'Created At' },
                ]}
              />
            </div>

            {dispatches.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-400 font-mono text-sm mb-4">NO DISPATCHES RECORDED YET.</p>
                {canDispatch && !isClosed && (
                  <button 
                    onClick={() => setShowDispatchDialog(true)}
                    className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6 mx-auto flex items-center justify-center gap-1.5"
                  >
                    <Plus className="h-4 w-4" /> ADD FIRST DISPATCH
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto border border-industrial-border">
                <table className="w-full text-xs font-mono text-left">
                  <thead>
                    <tr className="bg-industrial-s2/50 border-b border-industrial-border text-slate-400 text-[9px] uppercase tracking-wider">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Weight (kg)</th>
                      <th className="px-4 py-3">Remarks</th>
                      <th className="px-4 py-3">Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatches.map((d, idx) => (
                      <tr key={d.id} className={`border-b border-industrial-border/30 hover:bg-industrial-s2/40 ${idx % 2 === 0 ? 'bg-transparent' : 'bg-industrial-s2/10'}`}>
                        <td className="px-4 py-3 text-slate-300">{formatDate(d.dispatch_date)}</td>
                        <td className="px-4 py-3 text-right font-bold text-white">{formatWeight(d.dispatched_weight_kg)}</td>
                        <td className="px-4 py-3 text-slate-400">{d.remarks || '-'}</td>
                        <td className="px-4 py-3 text-[10px] text-slate-500">{formatDateTime(d.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {/* Create Program Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Create / Schedule Program</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 text-xs">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Circle Size (inch) *</label>
                <input 
                  type="number" 
                  step="0.1" 
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={newProgram.circle_size_inch} 
                  onChange={(e) => setNewProgram({...newProgram, circle_size_inch: e.target.value})} 
                />
              </div>
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Job Rate (₹/kg) *</label>
                <input 
                  type="number" 
                  step="0.01" 
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={newProgram.job_rate_per_kg} 
                  onChange={(e) => setNewProgram({...newProgram, job_rate_per_kg: e.target.value})} 
                />
              </div>
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Expected Recovery (%)</label>
              <input 
                type="number" 
                step="0.1" 
                min="0" 
                max="100" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={newProgram.expected_recovery_percent} 
                onChange={(e) => setNewProgram({...newProgram, expected_recovery_percent: e.target.value})} 
              />
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Remarks</label>
              <input 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={newProgram.remarks} 
                onChange={(e) => setNewProgram({...newProgram, remarks: e.target.value})} 
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
            <button 
              onClick={() => setShowCreateDialog(false)}
              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4"
            >
              CANCEL
            </button>
            <button 
              onClick={handleCreateProgram} 
              disabled={creating}
              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6"
            >
              {creating ? 'CREATING...' : 'CREATE'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Recovery Log Dialog */}
      <Dialog open={!!showRecoveryDialog} onOpenChange={() => { setShowRecoveryDialog(null); setRecoveryOcrData({ rawText: '', fileUrl: '' }); setNewRecoveryLog({ recovery_percent: '', strokes_per_min: '', slit_width_mm: '', photo_url: '', remarks: '' }); }}>
        <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Add Recovery Log</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 text-xs">
            <OCRUpload
              contextType="RECOVERY_LOG"
              partyId={coilIn?.party_id}
              onResult={({ rawText, fileUrl }) => {
                setRecoveryOcrData({ rawText, fileUrl });
                setNewRecoveryLog(prev => ({ ...prev, photo_url: fileUrl }));
                if (rawText) {
                  setNewRecoveryLog(prev => ({ ...prev, remarks: (prev.remarks ? prev.remarks + '\n' : '') + '[OCR] ' + rawText.slice(0, 200) }));
                }
              }}
              label="Upload Panel Photo (optional)"
            />
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Recovery % *</label>
              <input 
                type="number" 
                step="0.1" 
                min="0" 
                max="100" 
                placeholder="e.g. 72.5" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={newRecoveryLog.recovery_percent} 
                onChange={(e) => setNewRecoveryLog({...newRecoveryLog, recovery_percent: e.target.value})} 
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Strokes/min</label>
                <input 
                  type="number" 
                  step="1" 
                  min="0" 
                  placeholder="Optional" 
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={newRecoveryLog.strokes_per_min} 
                  onChange={(e) => setNewRecoveryLog({...newRecoveryLog, strokes_per_min: e.target.value})} 
                />
              </div>
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Slit Width (mm)</label>
                <input 
                  type="number" 
                  step="0.1" 
                  min="0" 
                  placeholder="Optional" 
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={newRecoveryLog.slit_width_mm} 
                  onChange={(e) => setNewRecoveryLog({...newRecoveryLog, slit_width_mm: e.target.value})} 
                />
              </div>
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Remarks</label>
              <input 
                placeholder="Optional notes" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={newRecoveryLog.remarks} 
                onChange={(e) => setNewRecoveryLog({...newRecoveryLog, remarks: e.target.value})} 
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
            <button 
              onClick={() => { setShowRecoveryDialog(null); setRecoveryOcrData({ rawText: '', fileUrl: '' }); setNewRecoveryLog({ recovery_percent: '', strokes_per_min: '', slit_width_mm: '', photo_url: '', remarks: '' }); }}
              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4"
            >
              CANCEL
            </button>
            <button 
              onClick={handleAddRecoveryLog} 
              disabled={addingLog}
              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6"
            >
              {addingLog ? 'ADDING...' : 'ADD LOG'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dispatch Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={(open) => { setShowDispatchDialog(open); if (!open) setDispatchOcrData({ rawText: '', fileUrl: '' }); }}>
        <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Add Dispatch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 text-xs">
            <OCRUpload
              contextType="DISPATCH"
              partyId={coilIn?.party_id}
              onResult={({ rawText, fileUrl }) => {
                setDispatchOcrData({ rawText, fileUrl });
                if (rawText) {
                  setNewDispatch(prev => ({ ...prev, remarks: (prev.remarks ? prev.remarks + '\n' : '') + '[OCR] ' + rawText.slice(0, 200) }));
                }
              }}
              label="Upload Dispatch Slip (OCR)"
            />
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Dispatch Date *</label>
              <input 
                type="date" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={newDispatch.dispatch_date} 
                onChange={(e) => setNewDispatch({...newDispatch, dispatch_date: e.target.value})} 
              />
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Dispatched Weight (kg) *</label>
              <input 
                type="number" 
                step="0.01" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={newDispatch.dispatched_weight_kg} 
                onChange={(e) => setNewDispatch({...newDispatch, dispatched_weight_kg: e.target.value})} 
              />
            </div>
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Remarks</label>
              <input 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={newDispatch.remarks} 
                onChange={(e) => setNewDispatch({...newDispatch, remarks: e.target.value})} 
              />
            </div>
            {reconciliation && newDispatch.dispatched_weight_kg && (
              <div className="text-xs bg-industrial-s2 border border-industrial-border p-3 font-mono">
                <p className="text-slate-400">PROJECTED BALANCE: {((reconciliation.balance_circle_kg || 0) - parseFloat(newDispatch.dispatched_weight_kg || 0)).toFixed(2)} kg</p>
                {(reconciliation.balance_circle_kg || 0) - parseFloat(newDispatch.dispatched_weight_kg || 0) < 0 && (
                  <p className="text-led-red font-bold uppercase mt-1">⚠️ OVER-DISPATCH DETECTED</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
            <button 
              onClick={() => { setShowDispatchDialog(false); setDispatchOcrData({ rawText: '', fileUrl: '' }); }}
              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4"
            >
              CANCEL
            </button>
            <button 
              onClick={handleAddDispatch} 
              disabled={addingDispatch}
              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6"
            >
              {addingDispatch ? 'ADDING...' : 'ADD DISPATCH'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Settlement Dialog */}
      <Dialog
        open={showSettlementDialog}
        onOpenChange={(open) => {
          setShowSettlementDialog(open);
          if (!open) {
            setSettlementForm({
              settlement_type: 'CIRCLE_ADJUSTMENT',
              quantity_kg: '',
              rate_per_kg: '',
              reason: '',
              remarks: '',
            });
          }
        }}
      >
        <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-lg p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Record Settlement</DialogTitle>
            <DialogDescription className="text-slate-400 font-mono text-xs mt-1">
              Use this only for owner-approved settlement adjustments. Every entry is audit logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 text-xs">
            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Settlement Type *</label>
              <select 
                value={settlementForm.settlement_type}
                onChange={(e) => setSettlementForm((prev) => ({ ...prev, settlement_type: e.target.value }))}
                className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
              >
                <option value="CIRCLE_ADJUSTMENT">Circle Adjustment</option>
                <option value="SCRAP_ADJUSTMENT">Scrap Adjustment</option>
                <option value="MATERIAL_BALANCE">Material Balance</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Quantity (kg) *</label>
                <input
                  type="number"
                  step="0.001"
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={settlementForm.quantity_kg}
                  onChange={(e) => setSettlementForm((prev) => ({ ...prev, quantity_kg: e.target.value }))}
                />
              </div>
              <div>
                <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Rate per kg (optional)</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                  value={settlementForm.rate_per_kg}
                  onChange={(e) => setSettlementForm((prev) => ({ ...prev, rate_per_kg: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Reason</label>
              <input
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={settlementForm.reason}
                onChange={(e) => setSettlementForm((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder="Owner approval reason"
              />
            </div>

            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Remarks</label>
              <textarea
                className="w-full min-h-[80px] p-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={settlementForm.remarks}
                onChange={(e) => setSettlementForm((prev) => ({ ...prev, remarks: e.target.value }))}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
            <button 
              onClick={() => setShowSettlementDialog(false)}
              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4"
            >
              CANCEL
            </button>
            <button 
              onClick={handleRecordSettlement} 
              disabled={recordingSettlement}
              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6"
            >
              {recordingSettlement ? 'SAVING...' : 'SAVE SETTLEMENT'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalize Dialog */}
      <Dialog open={showFinalizeDialog} onOpenChange={(open) => { setShowFinalizeDialog(open); if (!open) setFinalizeOcrData({ rawText: '', fileUrl: '' }); }}>
        <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-lg p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white flex items-center gap-2">
              <Lock className="h-5 w-5" /> Finalize Coil
            </DialogTitle>
            <DialogDescription className="text-slate-400 font-mono text-xs mt-1">
              Enter actual final circle and scrap weights. Closure happens only after all balances are settled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 text-xs">
            <OCRUpload
              contextType="FINALIZE"
              partyId={coilIn?.party_id}
              onResult={({ rawText, fileUrl }) => {
                setFinalizeOcrData({ rawText, fileUrl });
                if (rawText) {
                  setFinalizeData(prev => ({ ...prev, remarks: (prev.remarks ? prev.remarks + '\n' : '') + '[OCR] ' + rawText.slice(0, 200) }));
                }
              }}
              label="Upload Final Slip (OCR)"
            />
            
            {/* Current Summary */}
            <div className="bg-industrial-s2 border border-industrial-border p-4 font-mono">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Current Summary</p>
              <div className="grid grid-cols-2 gap-y-2 gap-x-4 text-[11px] text-slate-300">
                <div><span>Charge Weight:</span> <span className="font-bold text-white">{formatWeight(reconciliation?.charge_weight_kg)} kg</span></div>
                <div><span>Estimated Circle:</span> <span className="font-bold text-white">{formatWeight(reconciliation?.estimated_circle_kg)} kg</span></div>
                <div><span>Total Dispatched:</span> <span className="font-bold text-white">{formatWeight(reconciliation?.total_dispatched_kg || 0)} kg</span></div>
                <div><span>Balance:</span> <span className="font-bold text-white">{formatWeight(reconciliation?.balance_circle_kg)} kg</span></div>
              </div>
            </div>

            <div className="p-4 border border-accent bg-accent/5">
              <label className="block uppercase text-[10px] tracking-wider text-accent font-sans mb-2 font-bold">Final Circle Weight (kg) *</label>
              <input 
                type="number" 
                step="0.001" 
                placeholder="e.g. 1500.000" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={finalizeData.final_circle_weight_kg} 
                onChange={(e) => setFinalizeData({...finalizeData, final_circle_weight_kg: e.target.value})} 
              />
            </div>

            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Final Scrap Weight (kg) *</label>
              <input 
                type="number" 
                step="0.001" 
                placeholder="e.g. 203.000" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={finalizeData.scrap_weight_kg} 
                onChange={(e) => setFinalizeData({...finalizeData, scrap_weight_kg: e.target.value})} 
              />
            </div>

            <div>
              <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold">Remarks</label>
              <input 
                placeholder="Optional notes" 
                className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                value={finalizeData.remarks} 
                onChange={(e) => setFinalizeData({...finalizeData, remarks: e.target.value})} 
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
            <button 
              onClick={() => { setShowFinalizeDialog(false); setFinalizeOcrData({ rawText: '', fileUrl: '' }); }}
              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4"
            >
              CANCEL
            </button>
            <button 
              onClick={handleFinalize} 
              disabled={finalizing}
              className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6"
              data-testid="confirm-finalize-btn"
            >
              {finalizing ? 'SAVING...' : 'SAVE FINALIZATION'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Preview Modal */}
      <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
        <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-2xl p-6">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Panel Photo</DialogTitle>
          </DialogHeader>
          {previewPhoto && (
            <div className="flex flex-col items-center gap-4 py-4">
              <img 
                src={previewPhoto} 
                alt="Panel photo" 
                className="max-w-full max-h-[60vh] object-contain rounded-none border border-industrial-border"
              />
              <a 
                href={previewPhoto} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-accent hover:underline uppercase font-mono tracking-wider font-bold"
              >
                OPEN IN NEW TAB
              </a>
            </div>
          )}
          <DialogFooter className="border-t border-industrial-border pt-4 mt-2">
            <button 
              onClick={() => setPreviewPhoto(null)}
              className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-6"
            >
              CLOSE
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoilDetail;
